"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumAnnotationFileUtil = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const altiumFileTypes_1 = require("../../altiumFileTypes");
const altiumValidation_1 = require("../../altiumValidation");
class AltiumAnnotationFileUtil {
    constructor(parsedDef, projectConfigUtil) {
        this.projectConfigUtil = projectConfigUtil;
        this.designatorRecordDiagnostics = [];
        const multiPartIds = new Set();
        const annotationFileSelection = this.selectAnnotationFiles(parsedDef);
        this.annotationFiles = annotationFileSelection.files;
        this.annotationFileSelectionDiagnostics = annotationFileSelection.diagnostics;
        this.annotationFileSelectionBlockingDiagnostics = annotationFileSelection.blockingDiagnostics;
        const allAnnotations = this.getDesignatorAnnotations();
        for (const annotation of allAnnotations) {
            if (annotation.LogicalPartID !== '1') {
                multiPartIds.add(annotation.PhysicalDesignator);
            }
        }
        for (const multiPartId of multiPartIds) {
            for (const annotation of allAnnotations) {
                if (annotation.PhysicalDesignator === multiPartId) {
                    annotation._MultiPartId = true;
                }
            }
        }
        this.annotations = allAnnotations;
        this.annotationsByUniqueId = this.buildAnnotationsByUniqueIdMap(allAnnotations);
        this.channelNameByUniqueId = this.buildChannelNameMap(allAnnotations);
        this.documentNameByUniqueId = this.buildDocumentNameMap(allAnnotations);
        this.logicalReferenceDesignatorToAnnotation = this.buildLogicalReferenceDesignatorToAnnotationMap(allAnnotations);
        this.compiledSheetNumberDiagnostics = [
            ...this.annotationFileSelectionBlockingDiagnostics,
            ...this.annotationFiles.flatMap(({ fileName, annotationFile }) => annotationFile === undefined ? [`Project-listed annotation file ${fileName} is missing or ambiguous`] : []),
        ];
        this.sheetNumberManagerDiagnostics = this.annotationFiles.flatMap(({ fileName, annotationFile }) => (annotationFile?.SheetNumberManager?.Diagnostics ?? []).map((diagnostic) => `${fileName}: ${diagnostic}`));
    }
    selectAnnotationFiles(parsedDef) {
        const documentPathDiagnostics = this.projectConfigUtil.getAnnotationDocumentPathDiagnostics();
        if (documentPathDiagnostics.length > 0) {
            return {
                files: [],
                diagnostics: [...documentPathDiagnostics],
                blockingDiagnostics: [...documentPathDiagnostics],
            };
        }
        const annotationDescriptors = Object.values(parsedDef.other ?? {}).filter((file) => file.originalFileName.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase()));
        const files = [];
        const diagnostics = [];
        const listedDescriptorNames = new Set();
        for (const fileName of this.projectConfigUtil.getAnnotationDocumentPaths()) {
            const matches = annotationDescriptors.filter((file) => {
                const resolution = this.projectConfigUtil.resolveProjectDocumentPath(file.originalFileName);
                return resolution.documentPath === fileName;
            });
            if (matches.length === 0) {
                diagnostics.push(`annotation-manager-missing: ${fileName}`);
                continue;
            }
            for (const match of matches) {
                listedDescriptorNames.add(match.originalFileName);
            }
            files.push({ fileName, annotationFile: this.getSoleAnnotationFile(matches) });
        }
        const unlistedFileNames = annotationDescriptors
            .filter((file) => !listedDescriptorNames.has(file.originalFileName))
            .map((file) => file.originalFileName);
        if (unlistedFileNames.length > 0) {
            diagnostics.push(`annotation-manager-unlisted: ${unlistedFileNames.join(', ')}`);
        }
        if (files.length <= 1) {
            return { files, diagnostics, blockingDiagnostics: [] };
        }
        const projectFileName = this.projectConfigUtil.projectFileName;
        const projectStem = projectFileName === undefined ? undefined : this.getFileStem(projectFileName);
        const projectNamedFiles = files.filter(({ fileName }) => this.getFileStem(fileName) === projectStem);
        if (projectNamedFiles.length !== 1) {
            const diagnostic = `annotation-manager-ambiguous: ${files.map(({ fileName }) => fileName).join(', ')}`;
            return { files: [], diagnostics: [...diagnostics, diagnostic], blockingDiagnostics: [diagnostic] };
        }
        const selectedFile = projectNamedFiles[0];
        for (const file of files) {
            if (file !== selectedFile) {
                diagnostics.push(`annotation-manager-ignored: ${file.fileName}`);
            }
        }
        return { files: [selectedFile], diagnostics, blockingDiagnostics: [] };
    }
    getFileStem(fileName) {
        const basename = sigma_engine_shared_1.Files.basenamePath(fileName.replace(/\\/g, '/'));
        const extension = sigma_engine_shared_1.Files.extnamePath(basename);
        return basename.slice(0, basename.length - extension.length);
    }
    getSoleAnnotationFile(files) {
        if (files.length !== 1) {
            return undefined;
        }
        return files[0].validationResult?.data?.metadata?.[altiumValidation_1.AltiumProjectMetadataKey.Annotation];
    }
    get allAnnotations() {
        return this.annotations;
    }
    get numAnnotations() {
        return this.annotations.length;
    }
    get sheetNumberDiagnostics() {
        return [...this.compiledSheetNumberDiagnostics, ...this.sheetNumberManagerDiagnostics];
    }
    get annotationDiagnostics() {
        return this.designatorRecordDiagnostics;
    }
    getCompiledSheetNumber(pathUniqueId, sourceFile) {
        if (this.compiledSheetNumberDiagnostics.length > 0) {
            return {
                hasCompiledSheetNumberData: true,
                diagnostic: `Invalid compiled sheet-number manager cannot resolve instance path ${pathUniqueId} and source document ${sourceFile}: ${this.compiledSheetNumberDiagnostics.join('; ')}`,
            };
        }
        const documentName = this.projectConfigUtil.getAltiumDocumentConfiguration(sourceFile)?.DocumentPath;
        const invalidRecords = this.annotationFiles[0]?.annotationFile?.SheetNumberManager?.InvalidSheetNumbers ?? [];
        const invalidMatches = invalidRecords.filter((record) => (record.DocumentName !== undefined || record.UniqueIDPath !== undefined) &&
            (record.DocumentName === undefined ||
                this.normalizeSheetIdentity(record.DocumentName) === this.normalizeSheetIdentity(documentName)) &&
            (record.UniqueIDPath === undefined ||
                this.normalizeSheetIdentity(record.UniqueIDPath) === this.normalizeSheetIdentity(pathUniqueId)));
        if (invalidMatches.length > 0) {
            return {
                hasCompiledSheetNumberData: true,
                diagnostic: `Invalid compiled sheet-number assignment for instance path ${pathUniqueId} and source document ${sourceFile}; entries: ${invalidMatches.map((record) => record._Index).join(', ')}`,
            };
        }
        const records = this.annotationFiles[0]?.annotationFile?.SheetNumberManager?.SheetNumbers ?? [];
        let matches = records.filter((record) => record.DocumentName === documentName && record.UniqueIDPath === pathUniqueId);
        let matchedPathDescription = `instance path ${pathUniqueId}`;
        const separatorIndex = pathUniqueId.lastIndexOf('\\');
        if (matches.length === 0 && separatorIndex >= 0) {
            const parentPathUniqueId = pathUniqueId.substring(0, separatorIndex);
            matchedPathDescription = `parent instance path ${parentPathUniqueId}`;
            matches = records.filter((record) => {
                const recordSeparatorIndex = record.UniqueIDPath.lastIndexOf('\\');
                return (recordSeparatorIndex >= 0 &&
                    record.UniqueIDPath.substring(0, recordSeparatorIndex) === parentPathUniqueId &&
                    record.DocumentName === documentName);
            });
        }
        const fileNames = this.annotationFiles.map(({ fileName }) => fileName).join(', ');
        if (matches.length > 1) {
            return {
                hasCompiledSheetNumberData: true,
                diagnostic: `Multiple compiled sheet-number records match ${matchedPathDescription} and source document ${sourceFile}; files: ${fileNames}`,
            };
        }
        if (matches.length === 1) {
            return { hasCompiledSheetNumberData: true, sheetNumber: matches[0].SheetNumber };
        }
        return {
            hasCompiledSheetNumberData: false,
            diagnostic: `No compiled sheet-number record matches instance path ${pathUniqueId} and source document ${sourceFile}`,
        };
    }
    normalizeSheetIdentity(identity) {
        return identity?.trim().replace(/[\\/]+/g, '/');
    }
    getAnnotationsByUniqueId(uniqueId) {
        return Array.from(this.annotationsByUniqueId.get(uniqueId) ?? []);
    }
    getChannelNameByUniqueId(uniqueId) {
        return this.channelNameByUniqueId.get(uniqueId);
    }
    getDocumentNameByUniqueId(uniqueId) {
        return this.documentNameByUniqueId.get(uniqueId);
    }
    getAnnotationsByLogicalReferenceDesignator(referenceDesignator) {
        return Array.from(this.logicalReferenceDesignatorToAnnotation.get(referenceDesignator) ?? []);
    }
    buildAnnotationsByUniqueIdMap(annotations) {
        const map = new Map();
        for (const annotation of annotations) {
            const existing = map.get(annotation.UniqueID);
            if (existing) {
                existing.push(annotation);
            }
            else {
                map.set(annotation.UniqueID, [annotation]);
            }
        }
        return map;
    }
    buildChannelNameMap(annotations) {
        const map = new Map();
        const namesByChannel = new Map();
        for (const annotation of annotations) {
            const channelUniqueId = annotation.UniqueID.substring(0, annotation.UniqueID.lastIndexOf('\\'));
            const names = namesByChannel.get(channelUniqueId) ?? new Set();
            names.add(annotation.ChannelName);
            namesByChannel.set(channelUniqueId, names);
        }
        for (const [channelUniqueId, names] of namesByChannel) {
            if (names.size !== 1) {
                this.designatorRecordDiagnostics.push(`annotation-channel-name-conflict: ${channelUniqueId}: ${[...names].sort().join(', ')}`);
                continue;
            }
            for (const name of names) {
                map.set(channelUniqueId, name);
            }
        }
        return map;
    }
    buildDocumentNameMap(annotations) {
        const map = new Map();
        for (const annotation of annotations) {
            if (!map.has(annotation.UniqueID)) {
                map.set(annotation.UniqueID, annotation.DocumentName);
            }
        }
        return map;
    }
    buildLogicalReferenceDesignatorToAnnotationMap(annotations) {
        const map = new Map();
        for (const annotation of annotations) {
            const existing = map.get(annotation.LogicalDesignator);
            if (existing) {
                existing.push(annotation);
            }
            else {
                map.set(annotation.LogicalDesignator, [annotation]);
            }
        }
        return map;
    }
    getDesignatorAnnotations() {
        this.designatorRecordDiagnostics.push(...this.annotationFileSelectionDiagnostics);
        const selectedFile = this.annotationFiles[0];
        if (selectedFile === undefined) {
            return [];
        }
        if (selectedFile.annotationFile === undefined) {
            this.designatorRecordDiagnostics.push(`Project-listed annotation file is missing or ambiguous: ${selectedFile.fileName}`);
            return [];
        }
        const recordsByIdentity = new Map();
        for (const [index, record] of selectedFile.annotationFile.DesignatorManager.entries()) {
            if (record === undefined) {
                this.designatorRecordDiagnostics.push(`annotation-manager-record-missing: ${index}`);
                continue;
            }
            const identity = JSON.stringify([record.DocumentName, record.UniqueID, record.LogicalPartID]);
            const records = recordsByIdentity.get(identity) ?? [];
            records.push(record);
            recordsByIdentity.set(identity, records);
        }
        const selectedAnnotations = [];
        for (const [identity, records] of recordsByIdentity) {
            if (records.length === 1) {
                selectedAnnotations.push({ ...records[0] });
            }
            else {
                this.designatorRecordDiagnostics.push(`Conflicting designator records for ${identity}; file: ${selectedFile.fileName}`);
            }
        }
        return selectedAnnotations;
    }
}
exports.AltiumAnnotationFileUtil = AltiumAnnotationFileUtil;
//# sourceMappingURL=AltiumAnnotationFileUtil.js.map